var AnimationSupport    = require("enyo-anim/AnimationSupport"),
    Animator            = require("enyo-anim/Animator"),
    Attractor           = require("enyo-anim/Attractor"),
    Control             = require("enyo/Control"),
    EventEmitterSupport = require("enyo-ui/EventEmitterSupport"),
    PerformanceMode     = require("enyo-perf/PerformanceMode"),
    Scalar              = require("enyo-anim/Scalar"),
    kind                = require("enyo/kind");

require("enyo-ui");
require("./Drawer.css");

/**
 * Fires when the Drawer is open.
 *
 * @event     enyo-ui.Drawer#opened
 * @type      {object}
 * @property  {enyo.Control}  sender  The drawer that emitted the event
 */

/**
 * Fires when the Drawer is closed.
 *
 * @event     enyo-ui.Drawer#closed
 * @type      {object}
 * @property  {enyo.Control}  sender  The drawer that emitted the event
 */

/**
 * [Drawer]{@link enyo-ui.Drawer} is a simple container that can close and
 * expand to hide or reveal content.
 *
 * @ui
 * @class  enyo-ui.Drawer
 * @mixes  enyo-ui.AnimationSupport
 * @mixes  enyo-ui.EventEmitterSupport
 * @public
 */
module.exports = kind(/** @lends  enyo-ui.Drawer.prototype */ {

    /**
     * @private
     */
    name: "enyo-ui.Drawer",

    /**
     * @private
     */
    kind: Control,

    /**
     * @private
     */
    classes: "enyo-ui-drawer",

    /**
     * @private
     */
    mixins: [
        AnimationSupport,
        EventEmitterSupport
    ],

    /**
     * Whether the drawer is currently animating.
     *
     * @type  {boolean}
     * @readonly
     * @public
     */
    animating: false,

    /**
     * Whether to switch to performance mode during animation. This value should
     * not be toggled during an animation.
     *
     * @type    {boolean}
     * @default false
     * @public
     */
    usePerformanceMode: false,

    /**
     * The axis along which the drawer opens/closes.
     *
     * @type    {number}
     * @default "vertical"
     * @public
     */
    axis: "vertical",

    /**
     * The size of a closed drawer.
     *
     * @type    {number}
     * @default 0
     * @public
     */
    closedSize: 0,

    /**
     * Whether the drawer is open.
     *
     * @type    {boolean}
     * @default false
     * @readonly
     * @public
     */
    isOpen: false,

    /**
     * Whether the drawer is opening.
     *
     * @type    {boolean}
     * @default false
     * @readonly
     * @public
     */
    willOpen: false,

    /**
     * Whether the drawer is closing.
     *
     * @type    {boolean}
     * @default false
     * @readonly
     * @public
     */
    willClose: false,

    /**
     * Init size animator.
     *
     * @method
     * @private
     */
    initComponents: kind.inherit(function(sup) {
        return function() {
            this.size = new Animator({
                scalar: new Scalar({
                    value:   null,
                    epsilon: 0.01
                }),
                attractors: {
                    target: new Attractor({
                        attraction: 0.02
                    })
                }
            });
            sup.apply(this, arguments);
        };
    }),

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            this.updateTransition = this.bindSafely(this.updateTransition);
            sup.apply(this, arguments);
        };
    }),

    /**
     * @method
     * @private
     */
    rendered: kind.inherit(function(sup) {
        return function() {
            this.applyOpenOrCLosedStyles();
            sup.apply(this, arguments);
            this.isOpenChanged();
        };
    }),

    /**
     * @private
     */
    closedSizeChanged: function() {
        var closedSize = this.get("closedSize") || 0,
            animating  = this.animating;

        if (animating && this.willClose) {
            this.size.attractors.target.value = closedSize;
        }
        else if (!animating) {
            this.applyOpenOrCLosedStyles();
        }
    },

    /**
     * @private
     */
    axisChanged: function() {
        if (this.animating) {
            this.snapTransition();
        }
        this.applyOpenOrCLosedStyles();
    },

    /**
     * @private
     */
    applyOpenOrCLosedStyles: function() {
        var axis       = this.get("axis") || "vertical",
            closedSize = this.get("closedSize") || 0,
            isOpen     = this.isOpen;

        this.addStyles({
            "max-height": axis === "vertical" && !isOpen ? closedSize + "px" : null,
            "max-width":  axis !== "vertical" && !isOpen ? closedSize + "px" : null
        });
    },

    /**
     * A fading animation may still be active. We need to finish it, so it can
     * safely release performanceMode.
     *
     * @method
     * @private
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            this.animating && this.stopTransition();
            sup.apply(this, arguments);
        };
    }),

    // ...........................
    // Transition

    /**
     * Starts a transition animation.
     *
     * @private
     */
    startTransition: function() {
        var node = this.hasNode();

        if (this.animating || !node) {
            return;
        }

        this.size.scalar.value  = node.offsetHeight;
        this.usePerformanceMode && PerformanceMode.enable();
        this.set("animating", true);
        this.updateTransition();
    },

    /**
     * Update size animator, apply size, schedule next animation frame as
     * needed.
     *
     * @private
     */
    updateTransition: function() {
        var animator   = this.size,
            targetSize = animator.attractors.target.value,
            newSize;

        // Only during transition
        if (!this.animating) {
            this.warn("Don't call `updateTransition` if control is not animating.");

            return;
        }

        // Update size
        animator.update();
        newSize = animator.scalar.value;

        // Transition is finished
        if ((this.willClose && newSize <= targetSize)
            || (this.willOpen && newSize >= targetSize)) {
            animator.finish();
            this.applyTransition();
            this.stopTransition();
        }

        // Transition needs another frame
        else {
            this.applyTransition();
            this.requestAnimationFrame("updateTransition", this.updateTransition);
        }
    },

    /**
     * Applies the current size to the DOM.
     *
     * @private
     */
    applyTransition: function() {
        var animator   = this.size,
            newSize    = animator.scalar.value,
            targetSize = animator.attractors.target.value,
            axis       = this.get("axis");

        // Apply styles to DOM
        this.applyStyle(axis === "vertical" ? "max-height" : "max-width", newSize + "px");

        // Final state reached
        if (newSize === targetSize && !animator.isMoving()) {
            // Open state reached
            if (!this.isOpen && this.willOpen) {
                this.set("isOpen", true);
            }

            // Closed state reached
            else if (this.isOpen && this.willClose) {
                this.set("isOpen", false);
            }
        }
    },

    /**
     * Snaps transition to target and stops.
     *
     * @public
     */
    snapTransition: function() {
        this.size.snapToAttractor("target");
        this.applyTransition();
        this.stopTransition();
    },

    /**
     * Stops a transition animation.
     *
     * @public
     */
    stopTransition: function() {
        if (!this.animating) {
            return;
        }

        this.size.stop();
        this.cancelAnimationFrame("updateTransition");
        this.usePerformanceMode && PerformanceMode.disable();
        this.set("animating", false);
    },

    // ...........................
    // Events

    /**
     * @private
     */
    emitOpening: function() {
        this.emit("opening", {
            sender: this
        });
    },

    /**
     * @private
     */
    emitOpened: function() {
        this.emit("opened", {
            sender: this
        });
    },

    /**
     * @private
     */
    emitClosing: function() {
        this.emit("closing", {
            sender: this
        });
    },

    /**
     * @private
     */
    emitClosed: function() {
        this.emit("closed", {
            sender: this
        });
    },

    // ...........................
    // Open / Close

    /**
     * Open the drawer, optionally animate the transition too.
     *
     * @param  {boolean=}  animate  `true` if drawer should animate, `false`
     *                              otherwise
     * @public
     */
    open: function(animate) {
        var target = this.size.attractors.target,
            node;

        // Already open
        if (this.isOpen && !this.willClose) {
            return;
        }

        // Nothing is rendered, just set the flag and bail.
        if (!(node = this.hasNode())) {
            this.set("willClose", false);
            this.set("willOpen", false);
            this.set("isOpen", true);

            return;
        }

        target.value = node[this.get("axis") === "vertical" ? "scrollHeight" : "scrollWidth"];

        this.set("willClose", false);
        !this.isOpen && !this.willOpen && this.set("willOpen", true);

        if (animate === true) {
            this.emitOpening();
            this.startTransition();
        }
        else {
            this.snapTransition();
        }
    },

    /**
     * Close the drawer, optionally animate the transition too.
     *
     * @param  {boolean=}  animate  `true` if drawer should animate, `false`
     *                              otherwise
     * @public
     */
    close: function(animate) {
        var target = this.size.attractors.target;

        // Already closed
        if (!this.isOpen && !this.willOpen) {
            return;
        }

        // Nothing is rendered, just set the flag and bail.
        if (!this.hasNode()) {
            this.set("willOpen", false);
            this.set("willClose", false);
            this.set("isOpen", false);

            return;
        }

        target.value = this.get("closedSize");

        this.set("willOpen", false);
        this.isOpen && !this.willClose && this.set("willClose", true);

        if (animate === true) {
            this.emitClosing();
            this.startTransition();
        }
        else {
            this.snapTransition();
        }
    },

    /**
     * Unsets `willClose` and `willOpen` indicators when `isOpen` state changes
     * and fires `opened`/`closed` events as needed.
     *
     * @method
     * @private
     */
    isOpenChanged: function() {
        if (this.isOpen) {
            this.set("willOpen", false);
            this.set("willClose", false);
            this.emitOpened();
        }
        else {
            this.set("willOpen", false);
            this.set("willClose", false);
            this.emitClosed();
        }
    },

    // ...........................
    // Issers

    /**
     * Determines if drawer is animating.
     *
     * @return   {boolean}  `true` if animating, `false` otherwise.
     * @public
     */
    isAnimating: function() {
        return this.animating;
    },

    /**
     * Determines if drawer is opening.
     *
     * @return   {boolean}  `true` if opening, `false` otherwise.
     * @public
     */
    isOpening: function() {
        return this.willOpen;
    },

    /**
     * Determines if drawer is closing.
     *
     * @return   {boolean}  `true` if closing, `false` otherwise.
     * @public
     */
    isClosing: function() {
        return this.willClose;
    }

});
